import type { OptionSelection } from '@guildite/domain/listings';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

import { useCurrentUser } from '../../data/DataContext.tsx';

// The cart lives on the device (per account, or for a guest) until checkout.
// Prices here are only for display; the Worker recalculates everything.

export type CartItem = {
  key: string;
  listingId: string;
  shopId: string;
  shopName: string;
  shopHandle: string | null;
  name: string;
  photo: string | null;
  unitMinor: number;
  selection: OptionSelection;
  // The version picked (null: a listing not converted to versions yet).
  variantId?: string | null;
  summary: string;
  quantity: number;
  note: string;
  // A short stay: quantity is the number of nights and cannot be changed here.
  stay?: { from: string; to: string; guests: number };
};

type CartState = {
  items: CartItem[];
  count: number;
  add: (item: Omit<CartItem, 'key' | 'note'> & { note?: string }) => void;
  update: (key: string, patch: Partial<Pick<CartItem, 'quantity' | 'note'>>) => void;
  // Prices (and versions) as they are now (V2-022): the cart shows what checkout charges.
  reprice: (updates: Map<string, { unitMinor: number; variantId: string | null }>) => void;
  remove: (key: string) => void;
  removeShop: (shopId: string) => void;
  clear: () => void;
};

const CartContext = createContext<CartState | null>(null);
const MAX_QUANTITY = 99;

export function cartItemKey(listingId: string, selection: OptionSelection, stay?: CartItem['stay']): string {
  const sorted = Object.keys(selection).sort().map(group => [group, [...(selection[group] ?? [])].sort()]);
  return `${listingId}:${JSON.stringify(sorted)}${stay ? `:${stay.from}:${stay.to}` : ''}`;
}

function storageKey(uid: string | null) {
  return `guildite.cart.${uid ?? 'guest'}`;
}

function load(uid: string | null): CartItem[] {
  try {
    const value = JSON.parse(localStorage.getItem(storageKey(uid)) || '[]');
    return Array.isArray(value) ? value.filter(item => item && typeof item.listingId === 'string' && Number.isInteger(item.quantity)) : [];
  } catch {
    return [];
  }
}

export function CartProvider({ children }: { children: ReactNode }) {
  const user = useCurrentUser();
  const uid = user?.uid ?? null;
  const [state, setState] = useState<{ uid: string | null; items: CartItem[] }>(() => ({ uid, items: load(uid) }));
  const items = state.uid === uid ? state.items : load(uid);

  // A guest's cart moves into the account they sign in to.
  useEffect(() => {
    if (!uid) return;
    const guest = load(null);
    if (!guest.length) return;
    const merged = [...load(uid)];
    for (const item of guest) {
      const existing = merged.find(entry => entry.key === item.key);
      if (existing) existing.quantity = Math.min(MAX_QUANTITY, existing.quantity + item.quantity);
      else merged.push(item);
    }
    try {
      localStorage.setItem(storageKey(uid), JSON.stringify(merged));
      localStorage.removeItem(storageKey(null));
    } catch {
      // Storage unavailable: the cart lasts for this visit.
    }
  }, [uid]);

  const save = useCallback((next: CartItem[]) => {
    setState({ uid, items: next });
    try {
      localStorage.setItem(storageKey(uid), JSON.stringify(next));
    } catch {
      // Storage unavailable: the cart lasts for this visit.
    }
  }, [uid]);

  const value = useMemo<CartState>(() => ({
    items,
    count: items.reduce((sum, item) => sum + item.quantity, 0),
    add(item) {
      const key = cartItemKey(item.listingId, item.selection, item.stay);
      // One booking per place: new dates replace the old ones.
      const others = item.stay ? items.filter(entry => entry.listingId !== item.listingId) : items;
      const existing = others.find(entry => entry.key === key);
      save(existing
        ? others.map(entry => (entry.key === key ? { ...entry, quantity: Math.min(MAX_QUANTITY, entry.quantity + item.quantity), unitMinor: item.unitMinor } : entry))
        : [...others, { ...item, key, note: item.note ?? '' }]);
    },
    update(key, patch) {
      save(items.map(entry => (entry.key === key ? {
        ...entry,
        ...(patch.quantity !== undefined && !entry.stay ? { quantity: Math.max(1, Math.min(MAX_QUANTITY, Math.round(patch.quantity))) } : {}),
        ...(patch.note !== undefined ? { note: patch.note.slice(0, 200) } : {}),
      } : entry)));
    },
    reprice(updates) {
      const changed = (entry: CartItem) => {
        const update = updates.get(entry.key);
        return update && (update.unitMinor !== entry.unitMinor || update.variantId !== (entry.variantId ?? null));
      };
      if (items.some(changed)) save(items.map(entry => (changed(entry) ? { ...entry, ...updates.get(entry.key)! } : entry)));
    },
    remove: key => save(items.filter(entry => entry.key !== key)),
    removeShop: shopId => save(items.filter(entry => entry.shopId !== shopId)),
    clear: () => save([]),
  }), [items, save]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartState {
  const cart = useContext(CartContext);
  if (!cart) throw new Error('useCart must be used inside <CartProvider>.');
  return cart;
}
